import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Award, Users, Target, TrendingUp, Shield, ArrowLeft, Settings, Factory, Building2, Truck, Monitor, Heart, Layers } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import { RelatedCoursesSection } from '../components/training/RelatedCoursesSection';

export const FiveSTraining = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "5S Training in Chennai",
    "description": "Professional 5S training in Chennai for manufacturing, automotive, healthcare, IT, and logistics industries. Master workplace organization, efficiency, and safety with experienced 5S consultants.",
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy",
      "sameAs": "https://isde.academy"
    },
    "educationalLevel": "All Levels",
    "audience": {
      "@type": "Audience",
      "audienceType": "Production Staff, Quality Teams, Supervisors, Management, Office Staff, Warehouse Personnel"
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "Blended",
      "duration": "P1D"
    },
    "teaches": [
      "Seiri – Sort",
      "Seiton – Set in Order",
      "Seiso – Shine",
      "Seiketsu – Standardize",
      "Shitsuke – Sustain",
      "Visual management and color coding",
      "5S audits and performance monitoring"
    ],
    "geographicArea": {
      "@type": "City",
      "name": "Chennai",
      "containedInPlace": {
        "@type": "Country",
        "name": "India"
      }
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is 5S Training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "5S training is a structured programme that teaches the 5S methodology — Sort (Seiri), Set in Order (Seiton), Shine (Seiso), Standardize (Seiketsu), and Sustain (Shitsuke) — to create a clean, organized, and efficient workplace. It is the foundation of Lean manufacturing and operational excellence."
        }
      },
      {
        "@type": "Question",
        "name": "Why is 5S Training important in Chennai?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Chennai is a major hub for automotive, manufacturing, electronics, IT, and healthcare industries. 5S training in Chennai helps organizations eliminate workplace clutter, improve safety, reduce lead times, and build a culture of discipline and continuous improvement across all departments."
        }
      },
      {
        "@type": "Question",
        "name": "Who should attend 5S training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "5S training is suitable for all levels of an organization including top management, production and operations staff, quality and maintenance teams, supervisors and team leaders, office and administrative staff, and warehouse and logistics personnel. It applies to both shop floor and office environments."
        }
      },
      {
        "@type": "Question",
        "name": "What industries benefit from 5S training in Chennai?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "5S training benefits automotive and auto components, manufacturing and fabrication, electronics and electrical industries, healthcare and hospitals, warehousing and logistics, IT and corporate offices, and food processing and packaging industries in Chennai."
        }
      },
      {
        "@type": "Question",
        "name": "What are the key benefits of 5S training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Key benefits of 5S training include improved workplace organization and efficiency, reduction in waste, rework, and downtime, enhanced safety and fewer accidents, better space utilization, improved quality, faster retrieval of tools and materials, and a strong foundation for Lean, TPM, and Kaizen."
        }
      }
    ]
  };

  const fiveSModules = [
    {
      number: "1",
      japanese: "Seiri",
      english: "Sort",
      color: "red",
      topics: [
        "Identifying necessary and unnecessary items",
        "Red tag methodology",
        "Removing clutter and freeing up space"
      ]
    },
    {
      number: "2",
      japanese: "Seiton",
      english: "Set in Order",
      color: "orange",
      topics: [
        "Workplace layout planning",
        "Visual storage and labeling systems",
        "Tool boards, shadow boards, and color coding"
      ]
    },
    {
      number: "3",
      japanese: "Seiso",
      english: "Shine",
      color: "yellow",
      topics: [
        "Cleaning as a form of inspection",
        "Identifying abnormalities and defects",
        "Equipment care and preventive mindset"
      ]
    },
    {
      number: "4",
      japanese: "Seiketsu",
      english: "Standardize",
      color: "green",
      topics: [
        "Developing standard operating procedures (SOPs)",
        "Visual standards and checklists",
        "Ensuring consistency across departments"
      ]
    },
    {
      number: "5",
      japanese: "Shitsuke",
      english: "Sustain",
      color: "blue",
      topics: [
        "Building discipline and habits",
        "Employee involvement and ownership",
        "5S audits and performance monitoring"
      ]
    }
  ];

  const colorMap = {
    red: { bg: "bg-red-50", border: "border-red-200", badge: "bg-red-500 text-white", icon: "text-red-600", check: "text-red-500" },
    orange: { bg: "bg-orange-50", border: "border-orange-200", badge: "bg-orange-500 text-white", icon: "text-orange-600", check: "text-orange-500" },
    yellow: { bg: "bg-yellow-50", border: "border-yellow-200", badge: "bg-yellow-500 text-white", icon: "text-yellow-600", check: "text-yellow-500" },
    green: { bg: "bg-green-50", border: "border-green-200", badge: "bg-green-600 text-white", icon: "text-green-600", check: "text-green-500" },
    blue: { bg: "bg-blue-50", border: "border-blue-200", badge: "bg-blue-600 text-white", icon: "text-blue-600", check: "text-blue-500" }
  };

  const benefits = [
    { text: "Improved workplace organization and efficiency", icon: Settings },
    { text: "Reduction in waste, rework, and downtime", icon: TrendingUp },
    { text: "Enhanced safety and reduced accidents", icon: Shield },
    { text: "Better space utilization", icon: Layers },
    { text: "Improved quality and fewer errors", icon: CheckCircle },
    { text: "Faster retrieval of tools, materials, and documents", icon: Target },
    { text: "Strong foundation for Lean, TPM, and Kaizen", icon: Award },
    { text: "Improved employee ownership and morale", icon: Users }
  ];

  const whyChennaiChallenges = [
    "Workplace clutter and inefficiency",
    "Safety risks and accidents",
    "Poor material flow and longer lead times",
    "Inconsistent processes",
    "Low employee engagement"
  ];

  const targetAudience = [
    { title: "Top Management & Leadership", icon: Award, color: "blue" },
    { title: "Production & Operations Staff", icon: Factory, color: "orange" },
    { title: "Quality & Maintenance Teams", icon: Settings, color: "green" },
    { title: "Supervisors & Team Leaders", icon: Users, color: "purple" },
    { title: "Office & Administrative Staff", icon: Monitor, color: "indigo" },
    { title: "Warehouse & Logistics Personnel", icon: Truck, color: "teal" }
  ];

  const audienceColors = {
    blue: { bg: "bg-blue-100", text: "text-blue-600", border: "border-blue-200" },
    orange: { bg: "bg-orange-100", text: "text-orange-600", border: "border-orange-200" },
    green: { bg: "bg-green-100", text: "text-green-600", border: "border-green-200" },
    purple: { bg: "bg-purple-100", text: "text-purple-600", border: "border-purple-200" },
    indigo: { bg: "bg-indigo-100", text: "text-indigo-600", border: "border-indigo-200" },
    teal: { bg: "bg-teal-100", text: "text-teal-600", border: "border-teal-200" }
  };

  const industriesServed = [
    { name: "Automotive & Auto Components", icon: Factory },
    { name: "Manufacturing & Fabrication", icon: Settings },
    { name: "Electronics & Electrical", icon: Monitor },
    { name: "Healthcare & Hospitals", icon: Heart },
    { name: "Warehousing & Logistics", icon: Truck },
    { name: "IT & Corporate Offices", icon: Building2 },
    { name: "Food Processing & Packaging", icon: Layers }
  ];

  const whyChooseUs = [
    {
      title: "Experienced 5S Consultants",
      description: "Our trainers have hands-on experience in Lean and 5S implementation across multiple industries.",
      icon: Award
    },
    {
      title: "Practical and Results-Driven",
      description: "We focus on measurable improvements, not just classroom training.",
      icon: TrendingUp
    },
    {
      title: "Customized Training Approach",
      description: "Programs are designed based on your organization's size, industry, and maturity level.",
      icon: Target
    },
    {
      title: "End-to-End Support",
      description: "From awareness training to audits and sustainment, we support your complete 5S journey.",
      icon: Shield
    },
    {
      title: "High Return on Investment",
      description: "Our 5S training in Chennai delivers visible improvements in productivity, safety, and quality.",
      icon: CheckCircle
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="5S Training in Chennai | Workplace Organization & Safety | ISDE Academy"
        description="Professional 5S Training in Chennai for manufacturing, automotive, healthcare, IT industries. Master workplace organization, efficiency, and safety. Experienced trainers, customized programs."
        keywords="5S training Chennai, 5S training in Chennai, workplace organization Chennai, 5S methodology, lean manufacturing Chennai, kaizen training Chennai, 5S consultants Chennai, 5S training india"
        canonical="https://isde.academy/5s-training-in-chennai"
        ogUrl="https://isde.academy/5s-training-in-chennai"
        structuredData={[structuredData, faqStructuredData]}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-800 via-blue-900 to-blue-800 text-white py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Button
              variant="ghost"
              className="text-blue-200 hover:text-white mb-6 p-0"
              onClick={() => navigate('/industry-training/manufacturing')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Manufacturing Training
            </Button>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="bg-blue-500 bg-opacity-40 text-blue-100 text-sm font-medium px-3 py-1 rounded-full">
                Lean & Workplace Excellence
              </span>
              <span className="bg-orange-500 bg-opacity-40 text-orange-100 text-sm font-medium px-3 py-1 rounded-full">
                Chennai
              </span>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              5S Training
            </h1>

            <p className="text-lg md:text-xl text-blue-100 mb-4 max-w-4xl">
              In today's competitive business environment, organizations must continuously improve productivity, quality, and safety while reducing waste and inefficiencies. One of the most effective and widely adopted methodologies to achieve this is 5S. With Chennai emerging as a major hub for manufacturing, automotive, electronics, healthcare, logistics, and IT services, 5S Training in Chennai has become an essential requirement for organizations aiming to build operational excellence and workplace discipline.
            </p>
            <p className="text-base md:text-lg text-blue-200 max-w-4xl">
              Professional 5S training in Chennai helps organizations create a clean, organized, and standardized workplace that supports efficiency, safety, and continuous improvement. This page explains the importance of 5S, the benefits of structured training, and how organizations in Chennai can successfully implement and sustain 5S practices.
            </p>

            <div className="flex flex-wrap gap-4 mt-8">
              <Button
                size="lg"
                className="bg-white text-blue-900 hover:bg-blue-50 font-semibold"
                onClick={() => navigate('/contact')}
              >
                Enquire Now
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-blue-900 font-semibold"
                onClick={() => navigate('/training-calendar')}
              >
                View Schedule
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why 5S Training in Chennai is Important */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">Why 5S Training in Chennai Is Important</h2>
            <p className="text-gray-700 mb-6 text-lg">
              Chennai is one of India's leading industrial and commercial cities, home to automotive OEMs, auto component manufacturers, electronics companies, food processing units, IT parks, hospitals, and logistics hubs. Organizations operating in such fast-paced environments face challenges like:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
              {whyChennaiChallenges.map((challenge, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
                  <Shield className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 text-sm font-medium">{challenge}</span>
                </div>
              ))}
            </div>
            <p className="text-gray-700 text-lg">
              5S training in Chennai equips organizations with the knowledge and tools to address these challenges effectively. It provides a structured framework to eliminate waste, improve visibility, and establish discipline across all departments.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Key Benefits of 5S Training</h2>
            <p className="text-gray-600 mb-8 text-lg">
              Organizations that implement 5S through professional training experience measurable and sustainable benefits.
            </p>
            <h3 className="text-lg font-semibold text-gray-800 mb-5">Benefits of 5S Training Include:</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {benefits.map((benefit, idx) => {
                const Icon = benefit.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.05 }}
                    className="bg-white rounded-lg p-4 border border-gray-200 flex items-start gap-3 shadow-sm"
                  >
                    <Icon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-sm font-medium">{benefit.text}</p>
                  </motion.div>
                );
              })}
            </div>
            <p className="text-gray-600 mt-6">
              With proper 5S training, organizations often see visible improvements within a short period of time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 5S Training Modules */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">5S Training Modules Covered</h2>
            <p className="text-gray-600 text-lg">
              Our 5S training programme covers all five pillars of the methodology in depth, supported by practical exercises, workplace examples, and implementation tools.
            </p>
          </motion.div>

          {/* Introduction module */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-5"
          >
            <div className="flex items-center gap-4 mb-3">
              <span className="w-10 h-10 rounded-full bg-slate-700 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">0</span>
              <h3 className="text-xl font-bold text-gray-900">Introduction to 5S and Lean Thinking</h3>
            </div>
            <div className="ml-14 grid grid-cols-1 md:grid-cols-3 gap-2">
              {[
                "Overview of Lean principles",
                "Importance of workplace organization",
                "Relationship between 5S, productivity, and quality"
              ].map((point, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-slate-600 flex-shrink-0 mt-0.5" />
                  {point}
                </div>
              ))}
            </div>
          </motion.div>

          {/* 5S pillars */}
          <div className="space-y-5">
            {fiveSModules.map((mod, idx) => {
              const colors = colorMap[mod.color];
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.08 }}
                  className={`rounded-xl border ${colors.border} ${colors.bg} p-6`}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <span className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${colors.badge}`}>
                      {mod.number}
                    </span>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {mod.japanese} – {mod.english}
                      </h3>
                    </div>
                  </div>
                  <div className="ml-14 grid grid-cols-1 md:grid-cols-3 gap-3">
                    {mod.topics.map((topic, tIdx) => (
                      <div key={tIdx} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle className={`w-4 h-4 ${colors.check} flex-shrink-0 mt-0.5`} />
                        {topic}
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who Should Attend */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h4 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Who Should Attend 5S Training?</h4>
            <p className="text-gray-600 mb-8 text-lg">
              5S training is suitable for all levels of an organization. Since 5S principles apply to both shop floors and office environments, professional 5S training in Chennai can be customized for any type of organization.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {targetAudience.map((audience, idx) => {
                const Icon = audience.icon;
                const colors = audienceColors[audience.color];
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.07 }}
                  >
                    <Card className={`h-full border-2 ${colors.border} text-center`}>
                      <CardContent className="p-4">
                        <div className={`w-12 h-12 rounded-full ${colors.bg} flex items-center justify-center mx-auto mb-3`}>
                          <Icon className={`w-6 h-6 ${colors.text}`} />
                        </div>
                        <p className="text-sm font-semibold text-gray-800 leading-tight">{audience.title}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h4 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Industries We Serve in Chennai</h4>
            <p className="text-gray-600 mb-8 text-lg">
              Our 5S training in Chennai has been successfully delivered across various industries. Each training program is tailored to meet the specific operational needs of the industry.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {industriesServed.map((industry, idx) => {
                const Icon = industry.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.06 }}
                    className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center"
                  >
                    <Icon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <p className="text-xs font-semibold text-gray-700 leading-tight">{industry.name}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-14 bg-blue-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h4 className="text-2xl md:text-3xl font-bold mb-3">Why Choose Us for 5S Training in Chennai?</h4>
            <p className="text-blue-200 mb-8 text-lg">
              ISDE Academy delivers industry-proven 5S training programmes that go beyond theory to deliver real operational improvements.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {whyChooseUs.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.08 }}
                    className="bg-blue-800 bg-opacity-60 border border-blue-700 rounded-xl p-5"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Icon className="w-6 h-6 text-orange-400 flex-shrink-0" />
                      <h4 className="font-bold text-white">{item.title}</h4>
                    </div>
                    <p className="text-blue-200 text-sm">{item.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions About 5S Training in Chennai</h2>
            <div className="space-y-4">
              {faqStructuredData.mainEntity.map((faq, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-5">
                  <h3 className="font-semibold text-gray-900 mb-2">{faq.name}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{faq.acceptedAnswer.text}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Courses */}
      <RelatedCoursesSection currentSlug="iso-9001" slugs={['iso-9001', 'iatf-16949', 'iso-14001']} heading="Related ISO Training Courses" />

      {/* CTA */}
      <section className="py-14 bg-gradient-to-r from-slate-800 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to Implement 5S in Your Organisation?</h2>
            <p className="text-blue-200 mb-8 text-lg">
              Contact ISDE Academy to enrol your team in professional 5S training in Chennai. We deliver classroom and on-site programmes customized for manufacturing, healthcare, IT, and all industry types.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-blue-900 hover:bg-blue-50 font-semibold"
                onClick={() => navigate('/contact')}
              >
                Enquire About 5S Training
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-blue-900 font-semibold"
                onClick={() => navigate('/training-calendar')}
              >
                View Training Calendar
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
